






















































import { Component, Vue, Prop } from "vue-property-decorator";
import { StockPricePoint } from "@/interfaces/AiChartAnalysisVm";

@Component
export default class StockPriceChart extends Vue {
  @Prop({ required: true }) chartData!: StockPricePoint[];
  @Prop({ default: () => [] }) refData!: StockPricePoint[];
  @Prop({ default: () => [] }) fiftyDaysMovingAverageData!: StockPricePoint[];
  @Prop({ default: "" }) title!: string;
  @Prop({ default: null }) currency!: string | null;

  get percentageChange() {
    if (this.chartData.length < 2) return 0;
    const startPrice = this.chartData[0].close;
    const endPrice = this.chartData[this.chartData.length - 1].close;
    const percentageChange = ((endPrice - startPrice) / startPrice) * 100;
    return percentageChange.toFixed(2);
  }

  get percentageChangeLabel() {
    if (this.percentageChange > 0) return `+${this.percentageChange}%`;
    if (this.percentageChange < 0) return `${this.percentageChange}%`;
    return "0%";
  }

  get isDark() {
    // Vuetify exposes theme.dark — fallback to false if not available
    try {
      // cast to any to avoid TS errors if $vuetify is not typed here
      return !!((this as any).$vuetify && (this as any).$vuetify.theme && (this as any).$vuetify.theme.dark);
    } catch {
      return false;
    }
  }

  // Marker state: when set, tooltip percentage is calculated relative to this value
  markerValue: number | null = null;
  markerTimestamp: number | null = null;
  // context menu state for right-click
  contextMenu: { visible: boolean; x: number; y: number; nearestIndex: number | null } = { visible: false, x: 0, y: 0, nearestIndex: null };
  // dragging state
  draggingMarker: boolean = false;
  // hover state to change cursor when over marker
  markerHover: boolean = false;
  // custom hover tooltip
  hoverTooltip: { visible: boolean; x: number; y: number; dateStr: string; priceVal: number | null; maVal: number | null; refVal: number | null; pricePct: string; markerPct: string } =
    { visible: false, x: 0, y: 0, dateStr: '', priceVal: null, maVal: null, refVal: null, pricePct: '', markerPct: '' };
  // crosshair line + dots
  crosshair: { visible: boolean; x: number; plotTop: number; plotHeight: number; priceY: number | null; maY: number | null; refY: number | null } =
    { visible: false, x: 0, plotTop: 0, plotHeight: 0, priceY: null, maY: null, refY: null };


  onDataPointSelection(event: any, chartContext: any, config: any) {
    const idx = config && (Array.isArray(config.dataPointIndex) ? config.dataPointIndex[0] : config.dataPointIndex);
    if (idx == null || idx < 0) return;
    const point = this.chartData[idx];
    if (!point) return;
    this.markerTimestamp = new Date(point.date).getTime();
    this.markerValue = point.close;
  }

  openContextMenu(e: MouseEvent) {
    // attempt to map click x to chart x-axis timestamp using ApexCharts globals
    const apexRef: any = (this.$refs.apex as any);
    let nearestIdx: number | null = null;
    try {
      const chart = apexRef && (apexRef as any).chart;
      const globals = chart && chart.w && chart.w.globals;
      const chartEl = (apexRef as any).$el || (apexRef as any).chart && (apexRef as any).chart.el;
      if (globals && chartEl) {
        const minX = globals.minX;
        const maxX = globals.maxX;
        const rect = (chartEl as HTMLElement).getBoundingClientRect();
        const plotLeft = rect.left + (globals.translateX || 0);
        const plotWidth = globals.gridWidth || rect.width;
        const ratio = Math.min(1, Math.max(0, (e.clientX - plotLeft) / plotWidth));
        const ts = minX + ratio * (maxX - minX);
        // find nearest data index
        let bestDiff = Infinity;
        this.chartData.forEach((p, idx) => {
          const t = new Date(p.date).getTime();
          const diff = Math.abs(t - ts);
          if (diff < bestDiff) { bestDiff = diff; nearestIdx = idx; }
        });
      }
    } catch (err) {
      nearestIdx = null;
    }
    this.contextMenu = { visible: true, x: e.clientX, y: e.clientY, nearestIndex: nearestIdx };
    // close on outside click
    const close = (ev: MouseEvent) => {
      this.closeContextMenu();
      window.removeEventListener('click', close);
    };
    window.addEventListener('click', close);
  }

  onPointerHover(e: PointerEvent) {
    const near = !!this.markerTimestamp && this.isPointerNearMarker(e.clientX);
    this.markerHover = near;
    this.setChartCursor(near ? 'ew-resize' : '');
    // compute interpolated tooltip at exact mouse x
    try {
      const apexRef: any = (this.$refs.apex as any);
      const chart = apexRef && (apexRef as any).chart;
      const globals = chart && chart.w && chart.w.globals;
      const chartEl: HTMLElement = (apexRef && apexRef.$el) || (apexRef && apexRef.chart && apexRef.chart.el);
      if (globals && chartEl) {
        const rect = chartEl.getBoundingClientRect();
        const plotLeft = globals.translateX || 0;
        const plotWidth = globals.gridWidth || rect.width;
        const plotTop = globals.translateY || 0;
        const plotHeight = globals.gridHeight || 0;
        const x = e.clientX - rect.left;
        if (x >= plotLeft && x <= plotLeft + plotWidth) {
          const ratio = (x - plotLeft) / plotWidth;
          const ts = globals.minX + ratio * (globals.maxX - globals.minX);
          const date = new Date(ts);
          const dateStr = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
          const priceVal = this.interpolateAtTimestamp(this.chartData, ts);
          const maVal = this.fiftyDaysMovingAverageData && this.fiftyDaysMovingAverageData.length
            ? this.interpolateAtTimestamp(this.fiftyDaysMovingAverageData, ts) : null;
          const refVal = this.refData && this.refData.length
            ? this.interpolateAtTimestamp(this.refData, ts) : null;
          const startPrice = this.chartData && this.chartData.length ? this.chartData[0].close : 0;
          let pricePct = '';
          if (startPrice && priceVal != null) {
            const pct = ((priceVal - startPrice) / startPrice) * 100;
            pricePct = `\u0394 ${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`;
          }
          let markerPct = '';
          if (this.markerValue != null && priceVal != null && this.markerValue) {
            const pct = ((priceVal - this.markerValue) / this.markerValue) * 100;
            markerPct = `\u2691 ${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`;
          }
          this.hoverTooltip = { visible: true, x: e.clientX, y: e.clientY, dateStr, priceVal, maVal, refVal, pricePct, markerPct };
          // compute dot y positions via y-axis min/max
          const yMin = globals.minY;
          const yMax = globals.maxY;
          const valToY = (val: number) => plotTop + plotHeight - ((val - yMin) / (yMax - yMin)) * plotHeight;
          const priceY = priceVal != null ? valToY(priceVal) : null;
          const maY = maVal != null ? valToY(maVal) : null;
          const refY = refVal != null ? valToY(refVal) : null;
          this.crosshair = { visible: true, x, plotTop, plotHeight, priceY, maY, refY };
        } else {
          this.hoverTooltip.visible = false;
          this.crosshair.visible = false;
        }
      } else {
        this.hoverTooltip.visible = false;
        this.crosshair.visible = false;
      }
    } catch { this.hoverTooltip.visible = false; this.crosshair.visible = false; }
  }

  onPointerLeave() {
    this.hoverTooltip.visible = false;
    this.crosshair.visible = false;
    this.markerHover = false;
    this.setChartCursor('');
  }

  interpolateAtTimestamp(data: StockPricePoint[], ts: number): number | null {
    if (!data || data.length === 0) return null;
    const times = data.map(p => new Date(p.date).getTime());
    if (ts <= times[0]) return data[0].close;
    if (ts >= times[times.length - 1]) return data[times.length - 1].close;
    let lo = 0, hi = times.length - 1;
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (times[mid] <= ts) lo = mid; else hi = mid; }
    const ratio = (ts - times[lo]) / (times[hi] - times[lo]);
    return data[lo].close + ratio * (data[hi].close - data[lo].close);
  }

  setChartCursor(cursor: string) {
    try {
      const apexRef: any = (this.$refs.apex as any);
      const chartEl: HTMLElement = (apexRef && apexRef.$el) || (apexRef && apexRef.chart && apexRef.chart.el);
      if (!chartEl) return;
      const svgEl = chartEl.querySelector('svg');
      if (svgEl) (svgEl as unknown as HTMLElement).style.cursor = cursor;
      const canvas = chartEl.querySelector('.apexcharts-canvas') as HTMLElement | null;
      if (canvas) canvas.style.cursor = cursor;
    } catch { /* ignore */ }
  }

  isPointerNearMarker(clientX: number) {
    const apexRef: any = (this.$refs.apex as any);
    const chart = apexRef && (apexRef as any).chart;
    const globals = chart && chart.w && chart.w.globals;
    const chartEl = (apexRef as any).$el || (apexRef as any).chart && (apexRef as any).chart.el;
    if (!globals || !chartEl || this.markerTimestamp == null) return false;
    const rect = (chartEl as HTMLElement).getBoundingClientRect();
    const minX = globals.minX;
    const maxX = globals.maxX;
    // use plot-area offset + width to match where ApexCharts actually draws the annotation
    const plotLeft = rect.left + (globals.translateX || 0);
    const plotWidth = globals.gridWidth || rect.width;
    const ratio = (this.markerTimestamp - minX) / (maxX - minX);
    const markerScreenX = plotLeft + ratio * plotWidth;
    const dx = Math.abs(clientX - markerScreenX);
    const tolerance = 12; // px
    return dx <= tolerance;
  }

  maybeStartDrag(e: PointerEvent) {
    if (this.markerTimestamp == null) return;
    const near = this.isPointerNearMarker(e.clientX);
    if (!near) return;
    // start dragging via pointer events; prevent chart from starting a zoom selection
    this.draggingMarker = true;
    this.markerHover = true;
    (e as Event).preventDefault();
    (e as Event).stopPropagation();
    document.body.style.cursor = 'grabbing';
    this.setChartCursor('grabbing');
    const move = (ev: PointerEvent) => { ev.preventDefault(); this.handleDragMove(ev); };
    const up = (ev: PointerEvent) => { this.stopDrag(); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  handleDragMove(e: MouseEvent) {
    if (!this.draggingMarker) return;
    const apexRef: any = (this.$refs.apex as any);
    try {
      const chart = apexRef && (apexRef as any).chart;
      const globals = chart && chart.w && chart.w.globals;
      const chartEl = (apexRef as any).$el || (apexRef as any).chart && (apexRef as any).chart.el;
      if (!globals || !chartEl) return;
      const rect = (chartEl as HTMLElement).getBoundingClientRect();
      const minX = globals.minX;
      const maxX = globals.maxX;
      // use plot-area offset + width (same as isPointerNearMarker)
      const plotLeft = rect.left + (globals.translateX || 0);
      const plotWidth = globals.gridWidth || rect.width;
      const ratio = Math.min(1, Math.max(0, ((e as PointerEvent).clientX - plotLeft) / plotWidth));
      const ts = minX + ratio * (maxX - minX);
      // find nearest data index
      let bestDiff = Infinity;
      let nearestIdx: number | null = null;
      this.chartData.forEach((p, idx) => {
        const t = new Date(p.date).getTime();
        const diff = Math.abs(t - ts);
        if (diff < bestDiff) { bestDiff = diff; nearestIdx = idx; }
      });
      if (nearestIdx != null) {
        this.setMarkerAtIndex(nearestIdx);
      }
    } catch (err) {
      // ignore
    }
  }

  stopDrag() {
    this.draggingMarker = false;
    this.markerHover = false;
    document.body.style.cursor = '';
    this.setChartCursor('');
  }

  closeContextMenu() {
    this.contextMenu.visible = false;
    this.contextMenu.nearestIndex = null;
  }

  setMarkerAtIndex(idx: number | null) {
    if (idx == null || idx < 0) return;
    const p = this.chartData[idx];
    if (!p) return;
    this.markerTimestamp = new Date(p.date).getTime();
    this.markerValue = p.close;
  }

  setMarkerToStart() {
    if (!this.chartData || this.chartData.length === 0) return;
    const p = this.chartData[0];
    this.markerTimestamp = new Date(p.date).getTime();
    this.markerValue = p.close;
  }

  clearMarker() {
    this.markerTimestamp = null;
    this.markerValue = null;
  }


  get series() {
    const result: any[] = [
      {
        name: this.title || "Price",
        data: this.chartData.map(p => ({ x: new Date(p.date).getTime(), y: p.close }))
      }
    ];
    if (this.fiftyDaysMovingAverageData && this.fiftyDaysMovingAverageData.length) {
      result.push({
        name: "50d MA",
        data: this.fiftyDaysMovingAverageData.map(p => ({ x: new Date(p.date).getTime(), y: p.close }))
      });
    }
    if (this.refData && this.refData.length) {
      result.push({
        name: "Ref",
        data: this.refData.map(p => ({ x: new Date(p.date).getTime(), y: p.close }))
      });
    }
    return result;
  }

  get chartOptions() {
    const textColor = this.isDark ? "#ffffff" : "#222222";

    return {
      chart: {
        type: "area",
        zoom: { enabled: true },
        toolbar: { show: true }
      },
      dataLabels: { enabled: false },
      colors: ["#008FFB", "#FF4560", "#9E9E9E"],
      stroke: { curve: ["smooth", "smooth", "smooth"], width: [2, 2, 2] },
      fill: {
        type: ["gradient", "solid", "solid"],
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.05
        },
        opacity: [1, 0, 0]
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          style: { colors: textColor }
        }
      },
      yaxis: {
        labels: {
          formatter: (val: number) => val.toFixed(2),
          style: { colors: textColor }
        }
      },
      tooltip: {
        enabled: false
      },
      annotations: {
        xaxis: this.markerTimestamp
          ? [
              {
                x: this.markerTimestamp,
                borderColor: "#ffa600",
                label: { text: "Marker", style: { color: "#ffffff", background: "#ffa600" } }
              }
            ]
          : []
      },

      legend: {
        show: true,
        labels: { colors: textColor }
      },
      title: { text: `Seit Chart-Anfang: ${this.percentageChangeLabel}`, align: "left", style: { color: textColor } }
    };
  }
}
